import { useEffect, useState, useRef, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetAuthReentryData, useSubmitAuthReentryData } from '@egw/api'
import { Alert, AlertType, GenericError, OtpInput } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { CheckIcon } from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { formatPhoneNumber } from '../../../utils/phone-number'
import { Layout, PageHeader } from '../../../components'
import { AUTH_ROUTES } from '../../../constants'

const log = new Logger('Auth - ReEntry - VerifyPhone')

export const VerifyPhonePage: FC = () => {
  const { t } = useTranslation('features-auth')
  const { navigate } = useRouter()
  const [isInvalidToken, setIsInvalidToken] = useState(false)

  const { data, isLoading, error } = useGetAuthReentryData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitAuthReentryData()
  const { mutate: resend, isLoading: isResendSubmitting, error: submitResendError } = useSubmitAuthReentryData()
  const {
    mutate: continueApplication,
    isLoading: isContinuingApplication,
    error: errorContinuingApplication,
  } = useSubmitAuthReentryData()

  const [codeResent, setCodeResent] = useState(false)
  const [counter, setCounter] = useState(0)
  const [otpValue, setOtpValue] = useState('')

  const handleOtpComplete = (otp: string): void => {
    void onSubmit(otp)
  }

  const handleOtpChange = (value: string): void => {
    const trimmedValue = value.slice(0, 6)
    setOtpValue(trimmedValue)
  }

  const phoneNumber = data?.phone?.phoneNumber || ''

  useEffect(() => {
    if (data?.error === 'SESSION_NOT_FOUND') {
      navigate(WEALTH_ROUTES.SIGN_IN.path)
    }
  }, [data?.error, navigate])

  const hasExecuted = useRef(false)

  // Intentionally separated. Handles an edge case where session is already restored
  useEffect(() => {
    if (data?.nextStep === 'SESSION_RESTORED' && !hasExecuted.current) {
      hasExecuted.current = true
      // Continue application
      void continueApplication(undefined).then((res) => {
        if (res?.path) {
          navigate(res.path)
        } else {
          // Default navigation for auth flow
          navigate(WEALTH_ROUTES.SIGN_IN.path)
        }
      })
    }
  }, [data?.nextStep, continueApplication, navigate])

  const {
    formState: { errors: formErrors, isValid },
  } = useForm({
    defaultValues: {
      code: '',
    },
  })

  const resendOtp = async (): Promise<void> => {
    setCodeResent(true)

    try {
      const response = await resend({
        triggerPhoneOtp: true,
      })

      // Navigate to Sign In since session expired
      if (response?.error === 'SESSION_NOT_FOUND') {
        navigate(WEALTH_ROUTES.SIGN_IN.path)
      }

      setTimeout(() => {
        setCounter(30)
        const intervalId = setInterval(() => {
          setCounter((prev) => {
            if (prev <= 1) {
              clearInterval(intervalId)
              setCodeResent(false)

              return 0
            }
            return prev - 1
          })
        }, 1000)
      }, 5000)
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onSubmit = async (otpCode: string): Promise<void> => {
    if (isValid) {
      setIsInvalidToken(false)
      try {
        const response = await mutate({
          phoneVerificationCode: otpCode,
        })

        if (response?.error === 'INVALID_VERIFICATION_CODE') {
          setIsInvalidToken(true)
        } else if (response?.nextStep === 'VERIFY_EMAIL') {
          navigate(AUTH_ROUTES.RE_ENTRY_VERIFY_EMAIL.path)
        } else if (response?.nextStep === 'SESSION_RESTORED') {
          // Continue application
          const responseContinueApplication = await continueApplication(undefined)
          if (responseContinueApplication?.path) {
            navigate(responseContinueApplication.path)
          } else {
            // Default navigation for auth flow
            navigate(WEALTH_ROUTES.SIGN_IN.path)
          }
        }
      } catch (e) {
        log.error('Error submitting', e)
      }
    }
  }

  return (
    <Layout>
      {/* Loading initial data to make sure the user has access to this screen and retrieve data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start mt-3xl">
          <PageHeader title={t('re_entry.verify_phone.title')} />
          <p className="typo-primary-base-regular text-text-meta mb-3xs">{t('re_entry.verify_phone.sent_to')}</p>
          <p className="typo-primary-base-semibold text-text-header mb-md">{formatPhoneNumber(phoneNumber || '')}</p>

          <OtpInput
            value={otpValue}
            onChange={handleOtpChange}
            onComplete={handleOtpComplete}
            className="mb-sm"
            testId="otp-input"
            error={isInvalidToken ? t('re_entry.verify_phone.code_expired') : ''}
          />

          <div className="flex items-baseline gap-4">
            <button
              onClick={() => void resendOtp()}
              disabled={codeResent || isSubmitting || isContinuingApplication}
              className={`typo-primary-sm-semibold text-left w-fit ${codeResent ? 'text-gray-gray-600' : ' text-text-action'}`}
              type="button"
            >
              {t('re_entry.verify_phone.resend_message')}
            </button>
            {codeResent && !counter ? (
              <p className="typo-primary-sm-semibold text-text-body-success flex items-center gap-2">
                {t('re_entry.verify_phone.message_sent')} <CheckIcon className="size-5" />
              </p>
            ) : null}
          </div>

          {counter ? (
            <p className="typo-primary-sm-semibold text-text-placeholder mt-2xs">
              {t('re_entry.verify_phone.send_another_message', {
                time: counter,
              })}
            </p>
          ) : null}

          <div className="flex-1 md:hidden" />

          {submitError || errorContinuingApplication ? (
            <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
          ) : null}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </Layout>
  )
}
